<template>
  <div
    v-if="flow.step === 'quiz'"
    class="space-y-4"
  >
    <p
      v-if="flow.quizFailed"
      class="rounded-xl bg-error/10 p-4 text-sm"
    >
      {{ flow.quizResult?.description || 'بر اساس پاسخ‌ها این خدمت الان مناسب نیست. می‌توانید مشاوره بگیرید.' }}
    </p>
    <LeadQuestionFields
      v-else
      v-model="flow.quizAnswers"
      :questions="flow.quizQuestions as any"
    />
  </div>

  <div
    v-else-if="flow.step === 'visit'"
    class="space-y-3"
  >
    <p
      v-if="flow.selectedSlotLabel"
      class="flex items-center gap-2 rounded-xl bg-elevated p-3 text-sm"
    >
      <UIcon
        name="i-lucide-calendar-clock"
        class="size-4 shrink-0 text-muted"
      />
      <span class="min-w-0">{{ flow.selectedSlotLabel }}</span>
    </p>

    <div class="grid gap-3 sm:grid-cols-2">
      <button
        v-if="flow.allowsInPerson"
        type="button"
        class="flex items-start gap-3 rounded-xl border p-4 text-start transition hover:border-primary hover:bg-primary/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        :class="flow.visitType === 'in_person' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-default'"
        @click="flow.chooseVisit('in_person')"
      >
        <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <UIcon
            name="i-lucide-hospital"
            class="size-5"
          />
        </span>
        <span class="min-w-0">
          <span class="block font-semibold">ویزیت حضوری</span>
          <span class="mt-1 block text-sm text-muted">مراجعه به مطب در زمان انتخاب‌شده</span>
        </span>
      </button>

      <button
        v-if="flow.allowsVirtual"
        type="button"
        class="flex items-start gap-3 rounded-xl border p-4 text-start transition hover:border-primary hover:bg-primary/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
        :class="flow.visitType === 'virtual' ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-default'"
        @click="flow.chooseVisit('virtual')"
      >
        <span class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <UIcon
            name="i-lucide-video"
            class="size-5"
          />
        </span>
        <span class="min-w-0">
          <span class="block font-semibold">ویزیت مجازی</span>
          <span class="mt-1 block text-sm text-muted">تماس تصویری از طریق لینک اختصاصی</span>
        </span>
      </button>
    </div>
  </div>

  <div
    v-else-if="flow.step === 'doctor'"
    class="grid gap-3 sm:grid-cols-2"
  >
    <button
      v-for="doctor in flow.doctors"
      :key="doctor.id"
      type="button"
      class="flex items-center gap-3 rounded-xl border p-3 text-start transition hover:border-primary hover:bg-primary/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:p-4"
      :class="flow.selectedDoctor?.id === doctor.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-default'"
      @click="flow.chooseDoctor(doctor)"
    >
      <UAvatar
        :src="doctor.avatarUrl || undefined"
        :alt="doctor.name"
        icon="i-lucide-user"
        size="lg"
      />
      <span class="min-w-0 flex-1">
        <span class="block truncate font-semibold">{{ doctor.name }}</span>
        <span
          v-if="doctor.specialty"
          class="mt-0.5 block truncate text-sm text-muted"
        >{{ doctor.specialty }}</span>
      </span>
      <UIcon
        v-if="flow.selectedDoctor?.id === doctor.id"
        name="i-lucide-check"
        class="size-5 shrink-0 text-primary"
      />
    </button>

    <p
      v-if="!flow.doctors.length"
      class="rounded-xl border border-dashed border-default p-6 text-center text-sm text-muted sm:col-span-2"
    >
      در حال حاضر پزشکی برای این خدمت فعال نیست.
    </p>
  </div>

  <div
    v-else-if="flow.step === 'slot'"
    class="min-w-0 space-y-5"
  >
    <div
      class="-mx-1 flex snap-x snap-mandatory gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      role="group"
      aria-label="انتخاب سریع روز"
    >
      <button
        v-for="chip in flow.dayChips"
        :key="chip.date"
        type="button"
        class="flex min-w-17 shrink-0 snap-start flex-col items-center gap-0.5 rounded-xl border px-2 py-2.5 text-xs transition"
        :class="chipClass(chip.date)"
        @click="flow.selectedDay = chip.date"
      >
        <span class="font-medium">{{ chip.weekday }}</span>
        <span class="tabular-nums opacity-80">{{ chip.day }}</span>
        <span
          v-if="chipSlotCount(chip.date) !== null"
          class="tabular-nums text-[10px] leading-tight opacity-70"
        >
          {{ chipSlotLabel(chip.date) }}
        </span>
      </button>
    </div>

    <UButton
      variant="ghost"
      color="neutral"
      size="sm"
      block
      class="sm:hidden"
      :icon="showCalendar ? 'i-lucide-chevron-up' : 'i-lucide-calendar-days'"
      @click="showCalendar = !showCalendar"
    >
      {{ showCalendar ? 'بستن تقویم' : 'انتخاب از تقویم' }}
    </UButton>

    <div class="grid min-w-0 gap-5 sm:grid-cols-[20rem_1fr]">
      <div class="min-w-0">
        <AppJalaliCalendar
          v-model="flow.selectedDay"
          :min-date="flow.minDate"
          :max-date="flow.maxDate"
          :available-dates="flow.availableDates"
          class="mx-auto w-full max-w-sm sm:max-w-none"
          :class="showCalendar ? 'block' : 'hidden sm:block'"
          @month-change="flow.onMonthChange"
        />
      </div>

      <div class="min-w-0">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p class="text-sm font-semibold">
            {{ flow.faDayLabel(flow.selectedDay) }}
          </p>
          <UBadge
            v-if="!flow.slotsLoading && flow.slots.length"
            color="neutral"
            variant="subtle"
            size="sm"
          >
            {{ flow.toFaDigits(flow.slots.length) }} نوبت خالی
          </UBadge>
        </div>

        <div
          v-if="flow.slotsLoading"
          class="grid grid-cols-3 gap-2 sm:grid-cols-4"
        >
          <USkeleton
            v-for="n in 8"
            :key="n"
            class="h-11"
          />
        </div>

        <div
          v-else-if="flow.slotGroups.length"
          class="space-y-4 sm:max-h-80 sm:overflow-y-auto sm:pe-1"
        >
          <div
            v-for="group in flow.slotGroups"
            :key="group.id"
          >
            <p class="mb-2 flex items-center gap-1.5 text-xs text-muted">
              <UIcon
                :name="group.icon"
                class="size-3.5"
              />
              {{ group.label }}
            </p>
            <div class="grid grid-cols-3 gap-2 sm:grid-cols-4">
              <UButton
                v-for="slot in group.items"
                :key="slot.startAtUtc"
                size="md"
                block
                class="h-11 justify-center tabular-nums"
                :variant="flow.selectedSlot?.startAtUtc === slot.startAtUtc ? 'solid' : 'outline'"
                :color="flow.altSlots.has(slot.startAtUtc) ? 'warning' : 'primary'"
                @click="flow.selectSlot(slot)"
              >
                {{ flow.toFaDigits(slot.time) }}
              </UButton>
            </div>
          </div>
        </div>

        <div
          v-else
          class="rounded-xl border border-dashed border-default p-6 text-center"
        >
          <UIcon
            name="i-lucide-calendar-off"
            class="mx-auto size-6 text-muted"
          />
          <p class="mt-2 text-sm text-muted">
            در این روز نوبت خالی نیست.
          </p>
          <UButton
            v-if="nextFreeDay"
            variant="link"
            size="sm"
            class="mt-1"
            @click="flow.selectedDay = nextFreeDay"
          >
            نزدیک‌ترین روز آزاد: {{ flow.faDayLabel(nextFreeDay) }}
          </UButton>
        </div>
      </div>
    </div>
  </div>

  <div
    v-else-if="flow.step === 'otp'"
    class="space-y-4"
  >
    <template v-if="!flow.otpSent">
      <UFormField label="شماره موبایل">
        <AuthPhoneInput
          v-model="flow.phone"
          @keyup.enter="flow.phoneComplete && flow.sendOtp()"
        />
      </UFormField>
    </template>

    <template v-else>
      <UFormField label="کد ۶ رقمی پیامک‌شده">
        <div
          dir="ltr"
          class="flex w-full justify-center"
        >
          <UPinInput
            v-model="flow.otpCode"
            otp
            dir="ltr"
            :length="6"
            size="xl"
            autofocus
            :disabled="flow.otpVerifying"
            :ui="{
              root: 'flex w-full max-w-96 gap-1.5 sm:gap-2.5',
              base: 'flex-1 min-w-0 h-14 px-0 text-center text-lg font-semibold tabular-nums sm:h-16 sm:text-xl',
            }"
            @complete="flow.verifyOtp"
          />
        </div>
      </UFormField>

      <div class="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-muted">
        <template v-if="flow.otpCooldown > 0">
          <UProgress
            :model-value="resendProgress"
            size="sm"
            class="w-20"
          />
          <span class="tabular-nums">ارسال مجدد تا {{ flow.otpCooldownLabel }}</span>
        </template>
        <UButton
          v-else
          variant="ghost"
          size="sm"
          color="primary"
          icon="i-lucide-refresh-cw"
          :loading="flow.otpSending"
          :disabled="flow.otpVerifying"
          @click="flow.sendOtp"
        >
          ارسال مجدد کد
        </UButton>

        <UButton
          variant="link"
          size="sm"
          color="neutral"
          icon="i-lucide-pencil"
          :disabled="flow.otpVerifying"
          @click="flow.editPhone"
        >
          ویرایش شماره
        </UButton>
      </div>
    </template>
  </div>

  <div
    v-else-if="flow.step === 'confirm'"
    class="space-y-4"
  >
    <dl class="divide-y divide-default overflow-hidden rounded-xl border border-default text-sm">
      <div
        v-for="row in flow.summaryRows"
        :key="row.label"
        class="flex items-start gap-2 px-3 py-2.5 sm:gap-3 sm:px-4"
      >
        <UIcon
          :name="row.icon"
          class="mt-0.5 size-4 shrink-0 text-muted"
        />
        <dt class="shrink-0 text-muted">
          {{ row.label }}
        </dt>
        <dd class="ms-auto min-w-0 text-end font-medium wrap-break-word">
          <!-- Latin/grouped numbers need their own LTR context, but the cell stays RTL-positioned. -->
          <span
            v-if="row.ltr"
            dir="ltr"
            class="inline-block tabular-nums"
          >{{ row.value }}</span>
          <template v-else>{{ row.value }}</template>
        </dd>
      </div>
    </dl>

    <div class="grid gap-4 sm:grid-cols-2">
      <UFormField
        label="نام بیمار"
        required
      >
        <UInput
          v-model="flow.firstName"
          size="xl"
          class="w-full"
          autocomplete="given-name"
          placeholder="نام"
        />
      </UFormField>
      <UFormField
        label="نام خانوادگی بیمار"
        required
      >
        <UInput
          v-model="flow.lastName"
          size="xl"
          class="w-full"
          autocomplete="family-name"
          placeholder="نام خانوادگی"
        />
      </UFormField>
    </div>

    <PatientRelationPicker
      v-if="flow.existingMembers.length"
      v-model="flow.relation"
      :members="flow.existingMembers"
    />

    <UFormField
      label="کد ملی بیمار"
      required
      :error="flow.nationalIdError || undefined"
    >
      <!-- The wrapper carries the LTR direction so the trailing check lands after the digits. -->
      <div dir="ltr">
        <UInput
          :model-value="flow.nationalId"
          :color="flow.nationalIdValid ? 'success' : undefined"
          :trailing-icon="flow.nationalIdValid ? 'i-lucide-check' : undefined"
          size="xl"
          type="tel"
          inputmode="numeric"
          maxlength="10"
          autocomplete="off"
          class="w-full"
          placeholder="0012345678"
          @update:model-value="flow.onNationalIdInput(String($event))"
        />
      </div>
    </UFormField>

    <UFormField
      label="توضیح برای پزشک"
      hint="اختیاری"
    >
      <UTextarea
        v-model="flow.note"
        :rows="2"
        :maxlength="500"
        class="w-full"
        placeholder="در صورت نیاز، علائم یا سابقهٔ بیماری را بنویسید."
      />
    </UFormField>

    <div
      v-if="flow.campaignEnabled"
      class="grid gap-4 sm:grid-cols-2"
    >
      <UFormField
        label="کد تخفیف"
        hint="اختیاری"
        :error="flow.couponPreviewError || undefined"
      >
        <div class="flex gap-2">
          <UInput
            v-model="flow.couponCode"
            size="xl"
            class="w-full"
            dir="ltr"
            placeholder="OFF20"
            @blur="flow.previewCoupon()"
          />
          <UButton
            variant="soft"
            :loading="flow.couponApplying"
            @click="flow.previewCoupon()"
          >
            اعمال
          </UButton>
        </div>
      </UFormField>
      <UFormField
        label="کد کارت هدیه"
        hint="اختیاری"
      >
        <UInput
          v-model="flow.giftCardCode"
          size="xl"
          class="w-full"
          dir="ltr"
          placeholder="GC-…"
        />
      </UFormField>
    </div>

    <div
      v-if="flow.paymentOptions.length > 1"
      class="space-y-2"
    >
      <p class="text-sm font-medium">
        روش پرداخت
      </p>
      <URadioGroup
        v-model="flow.payMethod"
        variant="card"
        :items="flow.paymentOptions"
      />
    </div>

    <PaymentsGatewayChoice
      v-if="flow.payMethod === 'gateway'"
      v-model="flow.paymentGateway"
      :gateways="flow.paymentGateways"
    />

    <div class="flex items-center justify-between gap-2 rounded-xl bg-elevated px-4 py-3">
      <span class="text-sm text-muted">مبلغ قابل پرداخت</span>
      <span class="font-semibold tabular-nums">{{ flow.amountDueLabel }}</span>
    </div>

    <p class="text-center text-xs text-muted">
      با ثبت نوبت، پیامک تأیید و یادآور برای شما ارسال می‌شود.
    </p>
  </div>

  <div
    v-else-if="flow.step === 'success'"
    class="space-y-4"
  >
    <BookingTicket
      v-if="flow.ticket"
      :ticket="flow.ticket"
      :title="successTitle"
      :description="successDescription"
    />
  </div>

  <!-- The step navigation lives in `BookingFlowNav` so the modal can pin it in its footer. -->
</template>

<script setup lang="ts">
import type { useBookingFlow } from '~/composables/useBookingFlow'
import AuthPhoneInput from '~/components/auth/PhoneInput.vue'
import BookingTicket from '~/components/blocks/BookingTicket.vue'

const props = defineProps<{
  flow: ReturnType<typeof useBookingFlow>
  successTitle?: string
  successDescription?: string
}>()

const RESEND_SECONDS = 60

/** The month grid is opt-in on phones; the day strip covers the common cases. */
const showCalendar = ref(false)

const resendProgress = computed(() =>
  Math.round(((RESEND_SECONDS - props.flow.otpCooldown) / RESEND_SECONDS) * 100),
)

function hasCapacity(date: string) {
  return props.flow.availableDates.includes(date)
}

function chipSlotCount(date: string): number | null {
  const count = props.flow.availableSlotCounts[date]
  return typeof count === 'number' ? count : null
}

function chipSlotLabel(date: string) {
  const count = chipSlotCount(date) ?? 0
  return `${props.flow.toFaDigits(count)} نوبت`
}

function chipClass(date: string) {
  if (props.flow.selectedDay === date) return 'border-primary bg-primary text-inverted'
  if (hasCapacity(date)) return 'border-default hover:border-primary'
  return 'border-default text-muted opacity-60'
}

/** Nearest day with capacity after the empty one the patient is looking at. */
const nextFreeDay = computed(() => {
  const current = props.flow.selectedDay
  return [...props.flow.availableDates].sort().find((date) => date > current) ?? null
})
</script>
